// @flow @jsx h
import { h, Component } from 'preact'
import styles from './App.css'
import { connect } from '../../lib/provider'
import { bem } from '@nrk/dh-utils'

import { Background } from './Background/'
import { StartView } from './Foreground/StartView'
import { ResultView } from './Foreground/ResultView'
import { ExplanationView } from './Foreground/ExplanationView'
import { QuestionView } from './Foreground/QuestionView'
import { Foreground } from './Foreground/'
import { Overlay } from './Overlay/'
import { Modal } from './Modal/'

import { Router } from './lib/Router'
import { Fullscreen } from './lib/Fullscreen'
import type { Results, Questions, Answers } from '../types'
import { PATHS } from '../constants/'

type Props = {
  questions: Questions,
  results: Results,
  sendGaEvent: Function
}

type State = {
  path: string,
  questionNumber: number,
  answers: Answers,
  isModalShowing: boolean
}

const { START_VIEW, EXPLANATION_VIEW, QUESTION_VIEW, RESULT_VIEW } = PATHS

const initialState = {
  path: START_VIEW,
  questionNumber: 0,
  answers: [],
  isModalShowing: false,
  isAndroidApp: false
}

class AppComponent extends Component<Props, State> {
  state = { ...initialState }

  componentDidMount(): void {
    this.setState({
      isAndroidApp: location.search.indexOf('viewContext=no.nrk.mobil.app') > -1
    })
  }

  toggleModal = () => {
    this.setState({ isModalShowing: !this.state.isModalShowing })
  }

  restart = () => {
    this.setState({ ...initialState, path: START_VIEW })
  }

  onAnswerClick = (isCorrectAnswer: boolean): void => {
    const { sendGaEvent, questions } = this.props
    const { questionNumber, answers } = this.state

    this.setState({
      path: EXPLANATION_VIEW,
      answers: answers.concat([
        {
          questionNumber,
          correct: isCorrectAnswer
        }
      ])
    })

    sendGaEvent(
      `ANSWER/${questions[questionNumber].description}/${
        isCorrectAnswer ? 'correct' : 'wrong'
      }`
    )
  }

  onNextClick = (): void => {
    const { questionNumber, answers } = this.state
    const { questions, sendGaEvent } = this.props
    const questionsLength = questions.length
    const answersLength = answers.length
    const finishedQuiz = questionsLength === answersLength
    const numberOfCorrect = answers.filter(answer => answer.correct).length

    this.setState({
      questionNumber: questionNumber + 1,
      path: finishedQuiz ? RESULT_VIEW : QUESTION_VIEW
    })

    sendGaEvent(`NEXT/${questions[questionNumber].description}`)
    if (finishedQuiz) {
      sendGaEvent(`RESULT/${numberOfCorrect}`)
    }
  }

  onStartClick = (): void => {
    this.props.sendGaEvent('START_QUIZ')
    this.setState({
      path: QUESTION_VIEW
    })
  }

  onShareClick = (e): void => {
    e.preventDefault()
    this.props.sendGaEvent('SHARE')
    const elm = e.target
    window.open(elm.getAttribute('href'), elm.getAttribute('target'))
  }

  render() {
    const { questions } = this.props
    const { questionNumber, answers, path, isModalShowing, isAndroidApp } = this.state
    const currentQuestion = questions[questionNumber]

    const positionOrigo = questions.length
    const currentPosition = answers.reduce(
      (pv, cv) => (cv.correct ? pv + 1 : pv - 1),
      positionOrigo
    )
    const maxScore = questions.length * 2
    const isCorrect = wasCorrect(answers)

    return (
      <Fullscreen class={bem(styles.app, { 'is-android-app': isAndroidApp })}>
        <Background
          currentPosition={currentPosition}
          maxScore={maxScore}
          isCorrect={isCorrect}
          isCaptionVisible={path === EXPLANATION_VIEW}
          isResultView={path === RESULT_VIEW}
          answers={answers}
        />
        <Foreground
          centered={[START_VIEW].indexOf(path) > -1}
          ariaHidden={isModalShowing}
        >
          <Router path={path}>
            <StartView path={START_VIEW} onStartClick={this.onStartClick} />
            <ResultView
              path={RESULT_VIEW}
              answers={answers}
              currentPosition={currentPosition}
              onShareClick={this.onShareClick}
            />
            <QuestionView
              path={QUESTION_VIEW}
              currentQuestion={currentQuestion}
              onAnswerClick={this.onAnswerClick}
            />
            <ExplanationView
              path={EXPLANATION_VIEW}
              currentQuestion={currentQuestion}
              isCorrect={isCorrect}
              onNextClick={this.onNextClick}
            />
          </Router>
        </Foreground>
        <Overlay
          questions={questions}
          questionNumber={questionNumber}
          answers={answers}
          path={path}
          toggleModal={this.toggleModal}
          restart={this.restart}
          ariaHidden={isModalShowing}
        />
        <Modal isShowing={isModalShowing} toggleModal={this.toggleModal} />
      </Fullscreen>
    )
  }
}

export const App = connect()(AppComponent)

function wasCorrect(answers: Answers) {
  const lastAnswer = answers[answers.length - 1]
  if (!lastAnswer) return false

  return answers[answers.length - 1].correct
}



// WEBPACK FOOTER //
// ./src/v1/components/App.js